import {Button, makeStyles} from "@material-ui/core";
import {Helmet} from "react-helmet";
import {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  getListParticipants,
  initParticipantListData,
  initParticipantListMsg,
  onStartAddParticipants,
  updateListDetailPage
} from "../../../../../store/actions/participant/list";
import {BackDropAnalyzeLoading} from "../../../../atoms/commonLoadings";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import UtilService from "../../../../../helper/service";
import ParticipantListDetailHeader from "./ListDetailHeader";
import ParticipantDotMenu from "./ParticipantDotMenu";
import CustomCheckBox from "../../../creator/experienceManager/participantsDashboard/component/CustomCheckbox";
import RemoveParticipantsModal from "./RemoveParticipants";
import constants from "../../../../../config/constants";
import AddParticipants from "./AddParticipants";
import ParticipantTagModal from "./TagModal";
import CustomSnackBar from "../../../creator/experienceManager/participantsDashboard/component/CustomSnackBar";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: '#F0F0F0',
    fontFamily: 'roboto-regular',
    height: '100%',
    overflowX: 'hidden',
  },
  content: {
    textAlign: 'center',
    padding: '20px 32px 100px 32px',
    width: '100%',
    '@media (min-width: 2000px)': {
      width: 1600
    },
    '@media (max-width: 600px)': {
      padding: '55px 10px 10px 10px'
    },
  },
  paper: {
    width: '100%',
    marginBottom: '16px',
    paddingBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: 10,
    color: '#263238',
    backgroundColor: '#fff'
  },
  table: {
    minWidth: 750,
    '@media (max-width: 600px)': {
      width: '100%',
      minWidth: 'unset'
    }
  },
  tableHeadText: {
    fontFamily: 'roboto-bold',
    fontSize: 12,
    textTransform: 'uppercase',
    lineHeight: 1.5,
    color: '#606B76',
    '@media (max-width: 1025px)': {
      fontSize: 10
    }
  },
  tableRowNormal: {
    fontFamily: 'roboto-regular',
    fontSize: 14,
    color: '#606B76',
    backgroundColor: '#FFFFFF !important',
  },
  tableRowHover: {
    backgroundColor: '#F2F7FC !important'
  },
  tableRowSelected: {
    backgroundColor: '#F2F7FC !important'
  },
  tableRoot: {
    width: '100%',
    paddingBottom: 20
  },
  loadMoreBtn: {
    padding: '6px 20px',
    background: '#017AEF',
    marginTop: 0,
    marginBottom: 25,
    color: '#fff',
    '&:hover': {
      background: '#0f9fef',
      color: '#fff',
    },
    textTransform: 'none',
  },
}));

interface IHeader {
  disablePadding?: boolean;
  id?: string;
  label?: string;
  numeric?: boolean;
  color?: string;
}

const headers: IHeader[] = [
  { id: 'email', numeric: false, disablePadding: false, label: 'EMAIL', color: 'primary' },
  { id: 'tag', numeric: false, disablePadding: false, label: 'TAGS', color: 'default' },
  { id: 'action_btn', numeric: false, disablePadding: false, label: '', color: 'default' },
];

const ParticipantListDetail = (props: any) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const {
    listDetailPage, listLoading, listParticipants, totalListParticipants,
    startAddParticipants, message, msgType
  } = useSelector((state: any) => state.participantList);
  const [listId, setListId] = useState('');
  const [hoverParticipantId, setHoverParticipantId] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<any[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [isAddTags, setIsAddTags] = useState(false);

  const [snackAlertType, setSnackAlertType] = useState('');
  const [snackAlertMsg, setSnackMsg] = useState('');
  const [isShowSnackAlert, setIsShowSnackAlert] = useState(false);

  useEffect(() => {
    if (props.match.params && props.match.params.list_id) {
      setListId(props.match.params.list_id);
    }
  }, [props.match.params]);

  useEffect(() => {
    const state = props.history.location.state;
    if (state) {
      if (state.listName) {
        localStorage.setItem('listName', state.listName);
        localStorage.setItem('listEditable', state.listEditable);
      }
    }
  }, [props.history.location]);

  useEffect(() => {
    if (listId) {
      dispatch(initParticipantListData());
      fetchParticipants(listDetailPage, listId);
    }
  }, [listId]);

  useEffect(() => {
    if (!listLoading && message && msgType) {
      setSnackAlertType(msgType);
      setSnackMsg(message);
      setIsShowSnackAlert(true);
      dispatch(initParticipantListMsg());
    }
  }, [listLoading, message, msgType]);


  const fetchParticipants = (page: number, list_id: string) => {
    const request = {
      page,
      group_id: Number(list_id)
    };
    dispatch(getListParticipants(apolloClient.client, request, true));
  };

  /**
   * This method will be executed when click "Add Tags" option
   * on the right dot menu
   */
  const onAddTagsFromDot = (participant: any) => {
    setSelectedParticipant(participant);
    setIsAddTags(true);
    setOpenTagModal(true);
  };

  /**
   * This method will be executed when click "Add Tags" option
   * on the header
   */
  const onAddTags = () => {
    setSelectedParticipant(null);
    setIsAddTags(true);
    setOpenTagModal(true);
  };

  /**
   * This method will be executed when click "Remove Tags" option
   * on the right dot menu
   * @param participant
   */
  const onRemoveTagsFromDot = (participant: any) => {
    setSelectedParticipant(participant);
    setIsAddTags(false);
    setOpenTagModal(true);
  };

  /**
   * This method will be executed when click "Remove Tags" option
   * on the header
   */
  const onRemoveTags = () => {
    setSelectedParticipant(null);
    setIsAddTags(false);
    setOpenTagModal(true);
  };

  const onClearSelected = () => {
    setSelectedParticipants([]);
  };

  /**
   * this method will be executed when click "Remove Participant" option
   * on the right dot menu
   */
  const onRemoveParticipant = (participant: any) => {
    setSelectedParticipant(participant);
    setOpenRemoveModal(true);
  };

  /**
   * this method will be executed when click "Remove Participants" option
   * on the header
   */
  const onRemoveParticipants = () => {
    setSelectedParticipant(null);
    setOpenRemoveModal(true);
  };

  const onCloseModals = () => {
    setSelectedParticipant(null);
    setOpenRemoveModal(false);
    setOpenTagModal(false);
    dispatch(onStartAddParticipants(false));
  };

  const handleParticipantCheck = useCallback(({}, participant: any) => {
    const _participant = selectedParticipants.find((p: any) => p.id === participant.id);
    if (_participant) {
      const newSelectedParticipants =
        selectedParticipants.filter((p: any) => p.id != participant.id);
      setSelectedParticipants(newSelectedParticipants);
    } else {
      const originalParticipants = [...selectedParticipants];
      originalParticipants.push(participant);
      setSelectedParticipants(originalParticipants);
    }
  }, [selectedParticipants]);

  const isParticipantChecked = useCallback((participantId: string) => {
    const selectedParticipant = selectedParticipants.find((p: any) => p.id === participantId);
    return !!selectedParticipant;
  }, [selectedParticipants]);

  const showLoadMore = useCallback(() => {
    if (totalListParticipants > 0) {
      return Math.ceil(totalListParticipants / constants.PARTICIPANTS_PER_PAGE) !== (listDetailPage + 1);
    }
    return false;
  }, [listDetailPage, totalListParticipants]);

  const onLoadMore = useCallback(() => {
    dispatch(updateListDetailPage(listDetailPage + 1));
    fetchParticipants(listDetailPage + 1, listId);
  }, [dispatch, fetchParticipants, updateListDetailPage, listDetailPage, listId]);

  const hideSnackAlert = () => {
    setIsShowSnackAlert(false);
  };

  const onSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedParticipants(listParticipants);
      return;
    }
    setSelectedParticipants([]);
  }, [listParticipants]);

  return (
    <div className={classes.root}>
      <Helmet title="Participant List Detail" />
      <BackDropAnalyzeLoading loading={listLoading} />
      <ParticipantListDetailHeader
        selectedParticipants={selectedParticipants}
        onAddTags={onAddTags}
        onRemoveTags={onRemoveTags}
        onRemoveParticipants={onRemoveParticipants}
        onClearSelected={onClearSelected}
      />
      <div className={classes.content}>
        <div className={classes.tableRoot}>
          <div className={classes.paper}>
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {listParticipants.length > 0 && (
                        <CustomCheckBox
                          isheader={'true'}
                          checked={listParticipants.length > 0 && selectedParticipants.length === listParticipants.length}
                          onChange={onSelectAll}
                        />
                      )}
                    </TableCell>
                    {headers.map((header: any) => (
                      <TableCell
                        key={header.id}
                        align="left"
                        padding="normal"
                        className={classes.tableHeadText}
                      >
                        <span>{header.label}</span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listParticipants.map((participant: any, index: number) => (
                    <TableRow
                      onMouseOver={() => setHoverParticipantId(participant.id)}
                      onMouseLeave={() => setHoverParticipantId('')}
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                      hover={participant.id === hoverParticipantId}
                      selected={isParticipantChecked(participant.id)}
                      aria-checked={isParticipantChecked(participant.id)}
                      classes={{
                        root: classes.tableRowNormal,
                        hover: classes.tableRowHover,
                        selected: classes.tableRowSelected,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <CustomCheckBox
                          key={participant.id}
                          checked={isParticipantChecked(participant.id)}
                          onClick={(event: any) => handleParticipantCheck(event, participant)}
                          inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${index}` }}
                        />
                      </TableCell>
                      <TableCell scope="row" align="left"> { participant.email } </TableCell>
                      <TableCell scope="row" align="left"> { UtilService.getParsedTags(participant.tags) } </TableCell>
                      <TableCell align="left" padding='none'>
                        {selectedParticipants.length === 0 && (
                          <ParticipantDotMenu
                            onAddTags={() => onAddTagsFromDot(participant)}
                            onRemoveTags={() => onRemoveTagsFromDot(participant)}
                            onRemoveParticipant={() => onRemoveParticipant(participant)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
        {showLoadMore() && (
          <Button className={classes.loadMoreBtn} onClick={onLoadMore}>
            Load more...
          </Button>
        )}
      </div>
      <AddParticipants
        listId={listId}
        open={startAddParticipants}
        onClose={onCloseModals}
        onClearSelected={onClearSelected}
      />
      <RemoveParticipantsModal
        listId={listId}
        open={openRemoveModal}
        selectedParticipant={selectedParticipant}
        selectedParticipants={selectedParticipants}
        onClearSelected={onClearSelected}
        onClose={onCloseModals}
      />
      <ParticipantTagModal
        listId={listId}
        open={openTagModal}
        onClose={onCloseModals}
        isAdding={isAddTags}
        onClearSelected={onClearSelected}
        selectedParticipant={selectedParticipant}
        selectedParticipants={selectedParticipants}
      />

      <CustomSnackBar type={snackAlertType} message={snackAlertMsg} open={isShowSnackAlert} onClose={hideSnackAlert} />
    </div>
  )
};

export default ParticipantListDetail;